// Single-fetch + cached read of /api/me. We treat the role list as
// session-stable: it changes only on Auth0 token refresh, which forces a
// page reload anyway (Auth0Provider's onRedirectCallback). One module-level
// cache keeps the hook free of refetch storms when many components mount
// concurrently (TriageLayout + TriagePage + CommandPalette all want it).

import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchCurrentUser, type CurrentUser } from '../config/api';

type State = {
  user: CurrentUser | null;
  loading: boolean;
  /** Auth0 error or /api/me network failure — both surface here. */
  error: Error | null;
};

let cachedPromise: Promise<CurrentUser> | null = null;

function loadUser(): Promise<CurrentUser> {
  if (cachedPromise) return cachedPromise;
  cachedPromise = fetchCurrentUser().catch((err) => {
    // Reset so the next mount can retry (e.g. after a transient 5xx).
    cachedPromise = null;
    throw err;
  });
  return cachedPromise;
}

export function useCurrentUser(): State & { isAdmin: boolean } {
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const [state, setState] = useState<State>({ user: null, loading: true, error: null });

  useEffect(() => {
    if (auth0Loading) return;
    if (!isAuthenticated) {
      setState({ user: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    loadUser()
      .then((user) => {
        if (!cancelled) setState({ user, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ user: null, loading: false, error: err as Error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, auth0Loading]);

  return { ...state, isAdmin: state.user?.is_admin ?? false };
}

// Allow auth resets (logout, session expiry) to drop the cached promise so
// the next sign-in re-fetches against the new token's claims.
export function resetCurrentUserCache(): void {
  cachedPromise = null;
}
